import { type PulseError, removeEmptyParams } from '@prenuvo/pulse';
import { useMutation, useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';

import { ENDPOINTS } from '@/api/endpoints';
import { httpClient } from '@/api/http-client';
import {
  BloodReportMetadata,
  BloodReportMetadataRequest,
  GetBloodReportPdfPreviewRequest,
  GetBloodReportPdfPreviewResponse,
  PatchBloodReportPanelRequest,
  PatchBloodReportPanelResponse,
  PatchBloodReportRequest,
  PatchBloodReportResponse,
  PostBloodReportAiGenerateRequest,
  PostBloodReportPdfPublishResponse,
  PostBloodReportPublishRequest,
  PostBloodReportRequest,
  PostBloodReportResponse,
} from '@/api/model';

import {
  applyOptimisticBloodReportPanelPatch,
  applyOptimisticBloodReportPatch,
  cancelAndSnapshotBloodReportMetadata,
  mergeBloodReportPanelIntoMetadata,
  PatchBloodReportOptimisticContext,
  rollbackBloodReportMetadataFromContext,
} from './blood-report-mutation.util';

const KEY = {
  GET_BLOOD_REPORT_METADATA: 'GET_BLOOD_REPORT_METADATA',
  GET_REPORT_PDF: 'GET_REPORT_PDF',
} as const;

const request = {
  getBloodReportMetadata: async (
    { payload: { params } }: BloodReportMetadataRequest,
    signal?: AbortSignal,
  ) => {
    const { data } = await httpClient.get<BloodReportMetadata>(ENDPOINTS.BLOOD_REPORTS.METADATA, {
      params: removeEmptyParams(params),
      signal,
    });

    return data;
  },
  getBloodReportPdfPreview: async (
    { payload: { params } }: GetBloodReportPdfPreviewRequest,
    signal?: AbortSignal,
  ) => {
    const { data } = await httpClient.get<GetBloodReportPdfPreviewResponse>(
      `${ENDPOINTS.BLOOD_REPORTS.BASE}/${params.report.bloodReportId}/pdf/preview`,
      { signal },
    );

    return data;
  },
  patchBloodReport: async ({ payload: { body, params } }: PatchBloodReportRequest) => {
    const { data } = await httpClient.patch<PatchBloodReportResponse>(
      `${ENDPOINTS.BLOOD_REPORTS.BASE}/${params.report.bloodReportId}`,
      { body },
    );

    return data;
  },
  patchBloodReportPanel: async ({ payload: { body, params } }: PatchBloodReportPanelRequest) => {
    const { data } = await httpClient.patch<PatchBloodReportPanelResponse>(
      `${ENDPOINTS.BLOOD_REPORTS.BASE}/${params.report.bloodReportId}/panels/${params.panel.panelId}`,
      { body },
    );

    return data;
  },
  postBloodReport: async ({ payload: { body } }: PostBloodReportRequest) => {
    const { data } = await httpClient.post<PostBloodReportResponse>(ENDPOINTS.BLOOD_REPORTS.BASE, {
      body,
    });

    return data;
  },
  postBloodReportAiGenerate: async ({ payload: { params } }: PostBloodReportAiGenerateRequest) => {
    const { data } = await httpClient.post<BloodReportMetadata>(
      `${ENDPOINTS.BLOOD_REPORTS.BASE}/${params.report.bloodReportId}/ai/generate`,
    );

    return data;
  },
  postBloodReportPdfPublish: async ({ payload: { params } }: PostBloodReportPublishRequest) => {
    const { data } = await httpClient.post<PostBloodReportPdfPublishResponse>(
      `${ENDPOINTS.BLOOD_REPORTS.BASE}/${params.report.bloodReportId}/pdf/publish`,
    );

    return data;
  },
};

const query = {
  useGetBloodReportMetadata: (
    { payload }: BloodReportMetadataRequest,
    config?: Partial<UseQueryOptions<BloodReportMetadata, PulseError>>,
  ) =>
    useQuery<BloodReportMetadata, PulseError>({
      enabled: !!payload.params.care_plan_id,
      queryFn: ({ signal }) => request.getBloodReportMetadata({ payload }, signal),
      queryKey: [KEY.GET_BLOOD_REPORT_METADATA, payload.params.care_plan_id],
      ...config,
    }),
  useGetBloodReportPdfPreview: (
    { payload }: GetBloodReportPdfPreviewRequest,
    config?: Partial<UseQueryOptions<GetBloodReportPdfPreviewResponse, PulseError>>,
  ) =>
    useQuery<GetBloodReportPdfPreviewResponse, PulseError>({
      enabled: !!payload.params.report.bloodReportId,
      queryFn: ({ signal }) => request.getBloodReportPdfPreview({ payload }, signal),
      queryKey: [KEY.GET_REPORT_PDF, payload.params.report.bloodReportId],
      ...config,
    }),
};

const mutation = {
  usePatchBloodReport: (carePlanId: string, onSuccess: () => void) => {
    const queryClient = useQueryClient();

    return useMutation<
      PatchBloodReportResponse,
      PulseError,
      PatchBloodReportRequest,
      PatchBloodReportOptimisticContext
    >({
      mutationFn: (payload) => request.patchBloodReport(payload),
      onError: (_error, _variables, context) => {
        rollbackBloodReportMetadataFromContext({
          context,
          queryClient,
          queryKey: [KEY.GET_BLOOD_REPORT_METADATA, carePlanId],
        });
      },
      onMutate: async (variables) => {
        const { previousMetadata } = await cancelAndSnapshotBloodReportMetadata({
          queryClient,
          queryKey: [KEY.GET_BLOOD_REPORT_METADATA, carePlanId],
        });

        if (previousMetadata && variables.payload.body) {
          queryClient.setQueryData<BloodReportMetadata>(
            [KEY.GET_BLOOD_REPORT_METADATA, carePlanId],
            applyOptimisticBloodReportPatch(previousMetadata, variables.payload.body),
          );
        }

        return { previousMetadata };
      },
      onSuccess: (data) => {
        queryClient.setQueryData<BloodReportMetadata>(
          [KEY.GET_BLOOD_REPORT_METADATA, carePlanId],
          data,
        );

        onSuccess();
      },
    });
  },
  usePatchBloodReportPanel: (carePlanId: string, onSuccess: () => void) => {
    const queryClient = useQueryClient();

    return useMutation<
      PatchBloodReportPanelResponse,
      PulseError,
      PatchBloodReportPanelRequest,
      PatchBloodReportOptimisticContext
    >({
      mutationFn: (payload) => request.patchBloodReportPanel(payload),
      onError: (_error, _variables, context) => {
        rollbackBloodReportMetadataFromContext({
          context,
          queryClient,
          queryKey: [KEY.GET_BLOOD_REPORT_METADATA, carePlanId],
        });
      },
      onMutate: async (variables) => {
        const { previousMetadata } = await cancelAndSnapshotBloodReportMetadata({
          queryClient,
          queryKey: [KEY.GET_BLOOD_REPORT_METADATA, carePlanId],
        });

        if (previousMetadata && variables.payload.body) {
          const { panelId } = variables.payload.params.panel;

          queryClient.setQueryData<BloodReportMetadata>(
            [KEY.GET_BLOOD_REPORT_METADATA, carePlanId],
            applyOptimisticBloodReportPanelPatch({
              panelId,
              patch: variables.payload.body,
              previous: previousMetadata,
            }),
          );
        }

        return { previousMetadata };
      },
      onSuccess: (data) => {
        const currentMetadata = queryClient.getQueryData<BloodReportMetadata>([
          KEY.GET_BLOOD_REPORT_METADATA,
          carePlanId,
        ]);

        if (currentMetadata) {
          queryClient.setQueryData<BloodReportMetadata>(
            [KEY.GET_BLOOD_REPORT_METADATA, carePlanId],
            mergeBloodReportPanelIntoMetadata(currentMetadata, data),
          );
        }

        onSuccess();
      },
    });
  },
  usePostBloodReport: () =>
    useMutation<PostBloodReportResponse, PulseError, PostBloodReportRequest>({
      mutationFn: (payload) => request.postBloodReport(payload),
    }),
  usePostBloodReportAiGenerate: () =>
    useMutation<BloodReportMetadata, PulseError, PostBloodReportAiGenerateRequest>({
      mutationFn: (payload) => request.postBloodReportAiGenerate(payload),
    }),
  usePostBloodReportPublish: () =>
    useMutation<PostBloodReportPdfPublishResponse, PulseError, PostBloodReportPublishRequest>({
      mutationFn: (payload) => request.postBloodReportPdfPublish(payload),
    }),
};

export const bloodReport = {
  KEY,
  mutation,
  query,
  request,
};
